import { useEffect, useState } from 'react';

interface Props {
  onBack: () => void;
}

interface LatestRelease {
  url: string;
  version: string;
  sizeMb: string | null;
}

const FALLBACK_RELEASE: LatestRelease = {
  url: '/releases/HeroExtract-Sync-Setup-0.1.1.exe',
  version: '0.1.1',
  sizeMb: null,
};

/**
 * Parses electron-updater's latest.yml just enough to find the installer
 * filename, version and size. Avoids pulling a YAML lib for a 6-line file.
 */
function parseLatestYml(text: string): LatestRelease | null {
  const versionMatch = text.match(/^version:\s*(.+)$/m);
  const pathMatch = text.match(/^path:\s*(.+)$/m);
  const sizeMatch = text.match(/^\s+size:\s*(\d+)/m);
  if (!versionMatch || !pathMatch) return null;
  const version = versionMatch[1].trim();
  const file = pathMatch[1].trim();
  const sizeMb = sizeMatch ? (Number(sizeMatch[1]) / (1024 * 1024)).toFixed(0) : null;
  return {
    url: `/releases/${file}`,
    version,
    sizeMb,
  };
}

/**
 * "Add a character" page. Encourages the desktop app (recommended) and keeps
 * the addon-only manual flow available behind an expandable "Advanced" block
 * for non-Windows users or those who prefer not to install software.
 */
export function AddCharacterView({ onBack }: Props) {
  const [release, setRelease] = useState<LatestRelease>(FALLBACK_RELEASE);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/releases/latest.yml', { cache: 'no-store' });
        if (!res.ok) return;
        const text = await res.text();
        const parsed = parseLatestYml(text);
        if (parsed && !cancelled) setRelease(parsed);
      } catch {
        // network/parse fail -> keep fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="add-view">
      <button className="add-view-back" onClick={onBack} aria-label="Back to armory">
        ← Back to armory
      </button>

      <h1 className="add-view-title">Add your character</h1>
      <p className="add-view-lede">
        Run a small Windows app while you play. It captures your gear, stats,
        and talents and uploads them live to this armory. Takes ~2 minutes to set up.
      </p>
      <p className="add-view-privacy">
        Captures <strong>profile, gear, stats, talents only</strong> — no chat,
        passwords, or account info.
      </p>

      <div className="add-view-download">
        <a href={release.url} download className="add-view-download-btn">
          ⬇ Download HeroExtract Sync (Windows)
        </a>
        <span className="add-view-download-note">
          v{release.version}{release.sizeMb ? ` · ~${release.sizeMb} MB` : ''} · auto-updates
        </span>
      </div>

      <ol className="add-view-steps">
        <li>
          <strong>Install &amp; open.</strong> Run the installer. Windows
          SmartScreen will probably warn you (it&rsquo;s unsigned) — click{' '}
          <em>More info</em> → <em>Run anyway</em>. Launch from Start Menu.
        </li>
        <li>
          <strong>Onboarding.</strong> The app walks you through it: pick your
          WoW folder, paste your key, click <em>Install addon</em>. Done.
        </li>
        <li>
          <strong>Play.</strong> When you want to push an update, type{' '}
          <code>/hex</code> then <code>/reload</code> in WoW. The app uploads
          automatically. Lives in your system tray; updates itself going forward.
        </li>
      </ol>

      <h2 className="add-view-troubleshooting-title">Get a key</h2>
      <p className="add-view-key-info">
        You should already have a personal key. If not, ping{' '}
        <strong>Winnie</strong> on Discord and one will be minted for you. The
        key tags your characters as yours and is stored encrypted on your
        machine (Windows DPAPI).
      </p>

      <details className="add-view-advanced">
        <summary className="add-view-advanced-summary">
          Advanced: I just want the addon (manual upload, Mac/Linux, etc.)
        </summary>
        <div className="add-view-advanced-body">
          <p className="add-view-advanced-lede">
            If you can&rsquo;t (or don&rsquo;t want to) run the desktop app —
            e.g. on Mac/Linux — you can use the addon directly and send the
            resulting <code>.lua</code> file manually.
          </p>

          <div className="add-view-download">
            <a href="/HeroExtract.zip" download className="add-view-download-btn">
              ⬇ Download HeroExtract.zip (addon only)
            </a>
            <span className="add-view-download-note">
              WoW 3.3.5 · ~6 KB
            </span>
          </div>

          <ol className="add-view-steps">
            <li>
              <strong>Install.</strong> Unzip so you get a folder named exactly{' '}
              <code>HeroExtract</code>. Move it into{' '}
              <code>&lt;your-WoW&gt;\Interface\AddOns\</code> (next to the{' '}
              <code>Blizzard_*</code> folders).
            </li>
            <li>
              <strong>Enable.</strong> Launch WoW. At the character screen, click
              <em> AddOns</em> (bottom-left) and make sure <code>HeroExtract</code>{' '}
              is ticked.
            </li>
            <li>
              <strong>Capture.</strong> Log in your character. Type <code>/hex</code>{' '}
              — you should see a green message like{' '}
              <code>[HeroExtract] captured &lt;Name&gt;-&lt;Realm&gt; …</code>.
              Then type <code>/reload</code> to flush it to disk.
            </li>
            <li>
              <strong>Find the file.</strong>{' '}
              <code>
                &lt;your-WoW&gt;\WTF\Account\&lt;YOURACCOUNT&gt;\SavedVariables\HeroExtract.lua
              </code>
            </li>
            <li>
              <strong>Send it to Winnie.</strong> Drop that <code>.lua</code> file on
              Discord. It&rsquo;ll get loaded into the armory.
            </li>
          </ol>

          <h3 className="add-view-troubleshooting-title">Troubleshooting</h3>
          <ul className="add-view-troubleshooting">
            <li>
              <em>Addon missing from the list</em> — the folder must be named
              exactly <code> HeroExtract</code> (not <code>HeroExtract-main</code>, etc.).
            </li>
            <li>
              <em>No file after <code>/reload</code></em> — log out fully once,
              log back in, try again.
            </li>
            <li>
              <em><code>/hex</code> says &ldquo;capture rejected&rdquo;</em> —
              character data wasn&rsquo;t ready yet. Wait a few seconds, try again.
            </li>
          </ul>
        </div>
      </details>

      <p className="add-view-back-link">
        <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
          ← Back to armory
        </a>
      </p>
    </div>
  );
}
